import React from 'react';
import { Link } from 'react-router-dom';

import { ReprocessEstimate } from 'src/api/functions/assetsApi';

import { OutputGroup, ReprocessSelection } from './reprocessOptions';
import styles from './ReprocessFlow.module.scss';

type ReviewSummaryProps = {
  groups: OutputGroup[];
  selection: ReprocessSelection;
  estimate?: ReprocessEstimate;
  loading?: boolean;
  error?: any;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** What will run, on what, and what it costs, before anything is queued. */
export const ReviewSummary: React.FC<ReviewSummaryProps> = ({
  groups,
  selection,
  estimate,
  loading,
  error,
}) => {
  const labelFor = (id: keyof ReprocessSelection, key: string) =>
    groups.find((g) => g.id === id)?.options.find((o) => o.key === key)?.label ?? key;

  const picked = groups
    .map((g) => ({ title: g.title, labels: selection[g.id].map((k) => labelFor(g.id, k)) }))
    .filter((g) => g.labels.length);

  const nothingApplies = estimate?.assets.filter((a) => !a.applies).length ?? 0;

  return (
    <div className={styles.review} data-test="reprocess-review">
      {estimate && (
        <p className={styles.lede}>
          Re-run on {plural(estimate.counts.image, 'image')} and {plural(estimate.counts.video, 'video')}
          {estimate.counts.other ? ` (${plural(estimate.counts.other, 'other file')})` : ''}.
        </p>
      )}

      <dl className={styles.summaryList}>
        {picked.map((g) => (
          <React.Fragment key={g.title}>
            <dt>{g.title}</dt>
            <dd>{g.labels.join(', ')}</dd>
          </React.Fragment>
        ))}
      </dl>

      {loading && <p className={styles.hint}>Working out the cost…</p>}

      {error && (
        <p className={styles.error} role="alert">
          {error.status === 403
            ? 'You do not have permission to reprocess these assets.'
            : error.message || 'Could not price this batch.'}
        </p>
      )}

      {estimate && estimate.skipped.length > 0 && (
        <div className={styles.warning} data-test="reprocess-skipped">
          {plural(estimate.skipped.length, 'asset')} will be skipped:{' '}
          {estimate.skipped.map((s) => `${s.id} (${s.reason === 'not_found' ? 'not found' : 'no access'})`).join(', ')}
        </div>
      )}

      {estimate && nothingApplies > 0 && (
        <p className={styles.hint}>
          {plural(nothingApplies, 'asset')} {nothingApplies === 1 ? 'has' : 'have'} nothing to re-run
          for the outputs chosen (for example, transcodes on an image) and will be left as is.
        </p>
      )}

      {estimate && estimate.billing === 'credits' && (
        <div className={styles.cost} data-test="reprocess-cost">
          {estimate.video_credits > 0 && (
            <div>
              Video: <strong>{plural(estimate.video_credits, 'credit')}</strong>, reserved when the batch
              is queued.
            </div>
          )}
          {estimate.image_addon_credits > 0 && (
            <div>
              Image AI addons: about <strong>{plural(estimate.image_addon_credits, 'credit')}</strong>,
              charged as each one runs.
            </div>
          )}
          {estimate.total === 0 && <div>No credits are charged for this batch.</div>}
          {estimate.balance !== null && <div className={styles.hint}>Balance: {estimate.balance} credits</div>}
        </div>
      )}

      {estimate && estimate.billing === 'minutes' && (
        <p className={styles.hint} data-test="reprocess-minutes">
          Video is billed as transcode minutes on your plan
          {estimate.video_source_seconds > 0
            ? ` (${Math.ceil(estimate.video_source_seconds / 60)} min of source video per transcode).`
            : '.'}
        </p>
      )}

      {estimate && !estimate.sufficient && (
        <div className={styles.error} role="alert" data-test="reprocess-insufficient">
          Not enough credits: this batch needs {estimate.video_credits} for video and the balance is{' '}
          {estimate.balance}. Nothing will be queued.{' '}
          <Link to="/admin/subscription">Add credits</Link>
        </div>
      )}
    </div>
  );
};

export default ReviewSummary;
